import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import chunk from 'lodash-es/chunk'
import {memo, useMemo} from 'react'

import {getDraftItemUpdateColumnAction} from '../../../features/grouping/helpers'
import type {GroupingMetadataWithSource} from '../../../features/grouping/types'
import {getGroupTestId} from '../../../helpers/table-group-utilities'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {Omnibar} from '../../omnibar/omnibar'
import {CELL_HEIGHT} from '../constants'
import {useTableOmnibarFocus} from '../hooks/use-table-omnibar-focus'
import type {TableDataType} from '../table-data-type'
import {TablePagination} from '../table-pagination'
import type {TableRowProps} from '../table-row'
import {TableRowChunk, VIRTUALIZATION_CHUNK_SIZE} from '../table-row-chunk'
import styles from './table-group-contents.module.css'

export const TABLE_GROUP_FOOTER_HEIGHT = CELL_HEIGHT
export const TABLE_GROUP_FOOTER_MARGIN_TOP = 1

type FooterProps = {
  groupId: string
  scrollRef: React.RefObject<HTMLElement | null>
  metadata: GroupingMetadataWithSource
  shouldDisableFooter: boolean
  footerPlaceholder: string
}

const TableGroupFooter: React.FC<FooterProps> = memo(function TableGroupFooterComponent({
  groupId,
  scrollRef,
  metadata,
  shouldDisableFooter,
  footerPlaceholder,
}) {
  const {omnibarRef, onFocus, onKeyDown} = useTableOmnibarFocus(groupId)

  const newItemAttributes = useMemo(() => {
    const updateAction = getDraftItemUpdateColumnAction(metadata.sourceObject)
    if (updateAction) {
      return {
        updateColumnActions: [updateAction],
        groupId: metadata.value,
      }
    }
  }, [metadata.sourceObject, metadata.value])

  const title = getGroupTestId(metadata.sourceObject)

  return (
    <div
      style={{
        height: `${TABLE_GROUP_FOOTER_HEIGHT}px`,
        // this is to make room for row selection boundary at end of a group:
        marginTop: `${TABLE_GROUP_FOOTER_MARGIN_TOP}px`,
      }}
      className={clsx(styles.Box, shouldDisableFooter && styles.disabledFooter)}
      {...testIdProps(`table-group-footer-${title}`)}
    >
      <Omnibar
        ref={omnibarRef}
        role="row"
        isFixed={false}
        newItemAttributes={newItemAttributes}
        defaultPlaceholder={footerPlaceholder}
        groupingMetadata={metadata}
        onKeyDown={onKeyDown}
        onInputFocus={onFocus}
        disabled={shouldDisableFooter}
        scrollRef={scrollRef}
      />
    </div>
  )
})

type Props = {
  groupId: string

  /**
   * Indicate whether the group is marked as "expanded" (showing items) or
   * "collapsed" (items hidden).
   *
   * If `isCollapsed` is true, nothing is rendered for the content of a group.
   */
  isCollapsed: boolean
  itemData: TableRowProps['data']
  /**
   * Metadata related to the provided group.
   */
  metadata: GroupingMetadataWithSource
  /**
   * Rows representing items in this group
   */
  rows: Array<Row<TableDataType>>

  scrollRef: React.RefObject<HTMLElement | null>

  /**
   * Whether or adding items via the group footer is disabled
   */
  shouldDisableFooter: boolean

  /**
   * Placeholder text for text input for adding items
   */
  footerPlaceholder: string

  /*
   * Whether or not items can be added to this group
   */
  isEditable: boolean
}

/**
 * Content component for a group when viewing the project in table mode.
 */
export function TableGroupContents({
  groupId,
  isCollapsed,
  itemData,
  isEditable,
  metadata,
  rows,
  scrollRef,
  shouldDisableFooter,
  footerPlaceholder,
}: Props) {
  if (isCollapsed) {
    return null
  }

  return (
    <>
      {chunk(rows, VIRTUALIZATION_CHUNK_SIZE).map((rowChunk, index) => (
        <TableRowChunk
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          rows={rowChunk}
          itemData={itemData}
        />
      ))}
      <TablePagination pageType={{groupId: metadata.value}} />
      {isEditable && (
        <TableGroupFooter
          groupId={groupId}
          scrollRef={scrollRef}
          metadata={metadata}
          shouldDisableFooter={shouldDisableFooter}
          footerPlaceholder={footerPlaceholder}
        />
      )}
    </>
  )
}
